import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Upload, Loader2, Download, Share2, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import ShareButton from './ShareButton';
import ComprehensiveAnalysisResults from './ComprehensiveAnalysisResults';
import RatingFeedback from './RatingFeedback';
import AIEnhancement from './AIEnhancement';
import { generateSingleAnalysisWord } from '@/utils/wordGenerator';
import { generatePDFFromHTML } from '@/utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentAnalysis = ({ userId, darkMode, user, authToken }) => {
  const [documentFile, setDocumentFile] = useState(null);
  const [customQuestion, setCustomQuestion] = useState('');
  const [analysisMode, setAnalysisMode] = useState('hybrid');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
                       'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                       'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                       'application/msword', 'application/vnd.ms-powerpoint', 'application/vnd.ms-excel'];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|docx?|pptx?|xlsx?)$/i)) {
      toast.error('Please upload a PDF, Word, PowerPoint, or Excel file');
      return;
    }

    // Validate file size (25MB)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File size must be less than 25MB');
      return;
    }

    setDocumentFile(file);
    toast.success(`File "${file.name}" loaded successfully`);
  };

  const handleAnalyze = async () => {
    console.log('=== Document Analysis Started ===');
    console.log('Document file:', documentFile);
    console.log('User ID:', userId);
    console.log('Analysis mode:', analysisMode);
    console.log('Custom question:', customQuestion);
    
    if (!documentFile) {
      toast.error('Please upload a document first');
      return;
    }

    if (!userId) {
      console.error('User ID is missing!');
      toast.error('User ID not found');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('user_id', userId);
      formData.append('audience_filters', JSON.stringify({}));
      formData.append('analysis_mode', analysisMode);
      if (customQuestion) {
        formData.append('custom_question', customQuestion);
      }

      console.log('Sending request to:', `${API}/analyze/document`);
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ':', pair[1]);
      }

      const response = await axios.post(`${API}/analyze/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minutes for document processing
      });

      console.log('Analysis response:', response.data);
      setResult(response.data);
      toast.success('Document analysis complete!');
    } catch (error) {
      console.error('Document Analysis error:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      
      let errorMessage = 'Analysis failed. Please try again.';
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Analysis timeout. Try a smaller document or simpler analysis mode.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!result) return;
    
    try {
      await generateSingleAnalysisWord(result);
      toast.success('Word report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to generate report');
    }
  };

  const handleDownloadPDF = async () => {
    if (!result) {
      toast.error('No analysis to download');
      return;
    }
    
    try {
      // Check if element exists before attempting PDF generation
      const element = document.getElementById('document-analysis-results-pdf');
      if (!element) {
        throw new Error('Analysis results not found. Please wait for the page to fully load.');
      }
      
      toast.loading('Generating PDF...', { id: 'pdf-gen' });
      
      // Small delay to ensure all content is rendered
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await generatePDFFromHTML('document-analysis-results-pdf', 'emoQ-document-analysis');
      toast.success('PDF report downloaded successfully!', { id: 'pdf-gen' });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`, { id: 'pdf-gen' });
    }
  };

  const metadata = result?.metadata || {};

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      {!result && (
        <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'border-green-100'} shadow-lg`}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <FileText className="w-6 h-6 text-green-600" />
              Document Analysis
            </CardTitle>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Upload PDF, Word, PowerPoint, or Excel documents for emotional intelligence analysis
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Upload */}
            <div className={`border-2 border-dashed rounded-lg p-8 text-center ${
              darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-green-200 bg-green-50'
            } ${documentFile ? 'border-green-500' : ''}`}>
              <input
                type="file"
                id="document-upload"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <label htmlFor="document-upload" className="cursor-pointer">
                {documentFile ? (
                  <div className="space-y-2">
                    <FileCheck className="w-12 h-12 mx-auto text-green-600" />
                    <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {documentFile.name}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {(documentFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <Button variant="outline" size="sm" onClick={(e) => { e.preventDefault(); setDocumentFile(null); }}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-gray-400" />
                    <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Click to upload document
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      PDF, DOCX, PPTX, XLSX • Max 25MB • Up to 100 pages
                    </p>
                  </div>
                )}
              </label>
            </div>

            {/* Custom Question */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                What would you like to know? (Optional)
              </label>
              <textarea
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                placeholder="e.g., 'How effective is this presentation for investors?' or 'What emotions does this proposal evoke?'"
                className={`w-full p-3 rounded-lg border ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                } focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                rows={3}
              />
            </div>

            {/* Analysis Mode */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Analysis Mode
              </label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={analysisMode === 'overall' ? 'default' : 'outline'}
                  onClick={() => setAnalysisMode('overall')}
                  size="sm"
                  className={analysisMode === 'overall' ? 'bg-green-600' : ''}
                >
                  Overall
                </Button>
                <Button
                  variant={analysisMode === 'hybrid' ? 'default' : 'outline'}
                  onClick={() => setAnalysisMode('hybrid')}
                  size="sm"
                  className={analysisMode === 'hybrid' ? 'bg-green-600' : ''}
                >
                  Hybrid (Recommended)
                </Button>
                <Button
                  variant={analysisMode === 'page-by-page' ? 'default' : 'outline'}
                  onClick={() => setAnalysisMode('page-by-page')}
                  size="sm"
                  className={analysisMode === 'page-by-page' ? 'bg-green-600' : ''}
                >
                  Page-by-Page
                </Button>
              </div>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {analysisMode === 'overall' && 'Analyze entire document as one piece'}
                {analysisMode === 'hybrid' && 'Overall analysis + highlights from key pages'}
                {analysisMode === 'page-by-page' && 'Detailed analysis of each page separately'}
              </p>
            </div>

            {/* Credit Cost Indicator */}
            <div className={`text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-3`}>
              <span className="inline-flex items-center gap-1">
                This analysis costs 
                <span className={`font-bold px-2 py-1 rounded-md ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                  20 credits ⚡
                </span>
              </span>
            </div>

            {/* Analyze Button */}
            <Button
              onClick={handleAnalyze}
              disabled={!documentFile || loading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyzing Document...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Analyze Document
                </>
              )}
            </Button>

            {loading && (
              <div className="space-y-3">
                <div className="text-center mb-3">
                  <Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-600" />
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 animate-pulse" style={{width: '65%'}}></div>
                </div>
                <p className={`text-sm text-center font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Processing document...
                </p>
                <p className={`text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  This may take 30-90 seconds depending on document size
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results Section */}
      {result && (
        <div className="space-y-6">
          {/* Header with Document Info */}
          <Card className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'border-green-100'} shadow-lg`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <FileCheck className="w-6 h-6 text-green-600" />
                    Document Analysis Complete
                  </CardTitle>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                      {metadata.document_type?.toUpperCase() || 'DOCUMENT'}
                    </Badge>
                    <Badge variant="outline">{metadata.total_pages || 0} Pages</Badge>
                    <Badge variant="outline">{metadata.total_images || 0} Images</Badge>
                    <Badge variant="outline">{metadata.analysis_mode || 'hybrid'}</Badge>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setResult(null)}>
                  New Analysis
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              onClick={handleDownloadPDF}
              className="gap-2 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700"
            >
              <FileText className="w-4 h-4" />
              Download PDF
            </Button>
            <Button
              onClick={handleDownloadReport}
              className="gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Download className="w-4 h-4" />
              Download Word
            </Button>
            <ShareButton analysisId={result.id} />
          </div>

          {/* Comprehensive Analysis Results - Same as other content types */}
          <div id="document-analysis-results-pdf">
            <ComprehensiveAnalysisResults result={result} darkMode={darkMode} />
          </div>

          {/* AI Enhancement Section */}
          <AIEnhancement 
            analysis={result} 
            darkMode={darkMode} 
            user={user}
            authToken={authToken}
          />

          {/* Rating & Feedback */}
          <div className="mt-8">
            <RatingFeedback analysisId={result.id} darkMode={darkMode} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentAnalysis;
